import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoggedInIcon, Person, SignOutIcon, SignUpIcon } from '@yme/assets/icons';
import { useAuthorization, useShowModalSessionExpired, useWindowSize } from '@yme/common/hooks';
import { setIsLoadedLogoutIframe } from '@yme/components/Header/ProfileMenu/redux/actions';
import { getFooter, getIsKioskVersion } from '@yme/selectors/common';
import { onOpenWindow } from '@yme/utils/common';
import { BLANK } from '@yme/utils/constants';
import { manageExternalLoginWindow } from '@yme/utils/manageExternalLoginWindow';
import { getAuthorizedStatus, getIsEmptyProfileData, getProfileMenuData, } from '../ProfileMenu/redux/selectors';
import { LOGIN_POPUP_HEIGHT, LOGIN_POPUP_WIDTH_DESKTOP, LOGIN_POPUP_WIDTH_MOBILE, LOGIN_POPUP_WIDTH_TABLET, } from './constants';
import { buildEndpointSignUp, getSingInLink } from './helpers';
const getPopupSizes = (isMobile, isTablet, isInfiniteDesktop) => {
    switch (true) {
        case isMobile:
            return {
                width: LOGIN_POPUP_WIDTH_MOBILE,
                height: LOGIN_POPUP_HEIGHT,
            };
        case isTablet:
            return {
                width: LOGIN_POPUP_WIDTH_TABLET,
                height: LOGIN_POPUP_HEIGHT,
            };
        case isInfiniteDesktop:
            return {
                width: LOGIN_POPUP_WIDTH_DESKTOP,
                height: LOGIN_POPUP_HEIGHT,
            };
        default:
            return {
                width: LOGIN_POPUP_WIDTH_MOBILE,
                height: LOGIN_POPUP_HEIGHT,
            };
    }
};
export const useProfileMenu = () => {
    const dispatch = useDispatch();
    const [dropDownList, setDropDownList] = useState([]);
    const [externalPopup, setExternalPopup] = useState(null);
    const isAuthorized = useSelector(getAuthorizedStatus);
    const isEmptyProfileData = useSelector(getIsEmptyProfileData);
    const isKioskVersion = useSelector(getIsKioskVersion);
    const { labels, clientId, myYamahaLink, callbackEndpointUri } = useSelector(getProfileMenuData);
    const { language } = useSelector(getFooter);
    const { checkUserSession, isExpiredSession } = useAuthorization();
    const { showModalSessionExpired } = useShowModalSessionExpired();
    const { isMobileView, isTabletView, isInfiniteDesktopView } = useWindowSize();
    const getPopupParams = useCallback(() => {
        const { width, height } = getPopupSizes(isMobileView, isTabletView, isInfiniteDesktopView);
        const left = window.innerWidth / 2 - Number(width) / 2 + window.screenLeft || window.screenX;
        const top = window.outerHeight / 2 - Number(height) / 2 + window.screenTop || window.screenY;
        return `width=${width},height=${height},left=${left},top=${top},scrollbars=1,resizable=1`;
    }, [isInfiniteDesktopView, isMobileView, isTabletView]);
    const signInHandler = useCallback(() => {
        if (isKioskVersion) {
            return;
        }
        const path = getSingInLink({
            myYamahaLink,
            clientId,
            callbackEndpointUri,
        });
        setExternalPopup(window.open(path, BLANK, `popup,${getPopupParams()}`));
    }, [callbackEndpointUri, clientId, getPopupParams, isKioskVersion, myYamahaLink]);
    const signUpHandler = useCallback(() => {
        if (isKioskVersion) {
            return;
        }
        const path = buildEndpointSignUp({
            myYamahaLink,
            language,
        });
        setExternalPopup(window.open(path, BLANK, `popup,${getPopupParams()}`));
    }, [getPopupParams, isKioskVersion, language, myYamahaLink]);
    const singOutHandler = useCallback(() => {
        dispatch(setIsLoadedLogoutIframe(true));
    }, [dispatch]);
    const myYamahaHandler = useCallback(async () => {
        const isExpired = await checkUserSession();
        isExpired ? singOutHandler() : onOpenWindow(myYamahaLink);
    }, [checkUserSession, myYamahaLink, singOutHandler]);
    useEffect(() => {
        if (isEmptyProfileData && isExpiredSession) {
            showModalSessionExpired({
                submitCallback: signInHandler,
            });
        }
    }, [isEmptyProfileData, isExpiredSession, showModalSessionExpired, signInHandler]);
    const UNAUTHORIZED_MENU = useCallback(() => [
        {
            iconType: LoggedInIcon,
            callback: signInHandler,
            value: labels.headerMenu_login_label,
        },
        {
            iconType: SignUpIcon,
            callback: signUpHandler,
            value: labels.headerMenu_signUp_label,
        },
    ], [labels, signInHandler, signUpHandler]);
    const AUTHORIZED_MENU = useCallback(() => [
        {
            iconType: Person,
            callback: myYamahaHandler,
            value: labels.headerMenu_myYamahaLink_label,
        },
        {
            iconType: SignOutIcon,
            callback: singOutHandler,
            value: labels.headerMenu_signOut_label,
        },
    ], [myYamahaHandler, labels, singOutHandler]);
    useEffect(() => (isAuthorized ? setDropDownList(AUTHORIZED_MENU) : setDropDownList(UNAUTHORIZED_MENU)), [AUTHORIZED_MENU, isAuthorized, UNAUTHORIZED_MENU]);
    useEffect(() => {
        if (!externalPopup)
            return;
        return manageExternalLoginWindow({
            externalPopup,
            setExternalPopup,
        });
    }, [externalPopup]);
    return {
        dropDownList,
        signInHandler,
        signUpHandler,
    };
};
